<!-- 
An entry in the history timeline.
-->
<template>
  <v-timeline-item
    :color="color"
    :icon="icon"
    :large="item.action === 'LevyCollectionProviderCreated' || item.action === 'LevyCollectionProviderStatusChanged'"
    fill-dot
  >
    <v-card :color="color" dark>
      <v-card-title 
        :class="`pl-3 pt-2 pr-2 pb-2 subheading ${item.detail ? 'expanding-header' : ''}`" 
        style="min-height:52px;" 
        @click="item.detail && $emit('update:expanded', !expanded)"
      >
        <span v-if="statusChangesOnly">
          {{ description }}
        </span>
        <span v-if="!statusChangesOnly">
          {{ action }}
        </span>
        <span v-if="!statusChangesOnly && description">
          &nbsp;({{ description }})
        </span>
        <v-spacer/>
        <v-btn v-if="item.detail" icon class="ma-0">
          <v-icon :class="'ml-1 expand-icon ' + (expanded ? 'expanded' : '')" style="position:relative;left:-1px;">expand_more</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="white text--primary">
        <v-layout row wrap>
          <v-flex xs12 sm9>
            <p class="caption mb-0">
              {{ item.date | longDateTime }}
            </p>
            <p class="caption mb-1">
              {{ item.userName }}
            </p>
          </v-flex>
          <v-flex xs12 sm3 text-xs-right>
            <v-btn 
              v-if="item.invoiceId" 
              :to="`/levy-collections/${levyCollectionId}/invoices/${item.invoiceId}`" 
              flat small color="primary"
            >
              View invoice
            </v-btn>
            <v-btn
              v-if="item.noticeId"
              flat small color="primary"
              @click="showNotice(item.noticeId)"
            >
              View notice
            </v-btn>
          </v-flex>
        </v-layout>
        <v-expand-transition>
          <!-- v-show instead of v-if because of jitter (fix if possible) -->
          <div v-show="expanded" class="caption">
            <div v-if="item.detail" class="pl-3 pt-3 pr-3 pb-0">
              <our-data-container dense fluid>
                <template v-for="(line, i) of item.detail">
                  <our-data-field :key="`${item.id}-${i}-t`" :label="line.field" label-width="12" offset="0">
                    <div v-if="line.value" :key="`${item.id}-${i}-d`">
                      <pre v-if="isMultiLine(line.value)" class="wrap highlight">{{ line.value }}</pre>
                      <template v-else>{{ line.value }}</template>
                    </div>
                    <div v-else :key="`${item.id}-${i}-d`">
                      <template v-for="(diff, j) of describeDifferences(line.before, line.after)">
                        <vue-code-diff 
                          v-if="isMultiLine(diff)" 
                          :key="`${item.id}-${i}-${j}`" 
                          :old-string="(line.before ? line.before : '')" 
                          :new-string="(line.after ? line.after : '')"
                        />
                        <p v-else :key="`${item.id}-${i}-${j}`" class="ma-0 pa-0">
                          {{ diff }}
                        </p>
                      </template>
                    </div>
                  </our-data-field>
                </template>
              </our-data-container>
            </div>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>
    <notice-view-dialog v-model="showNoticeViewDialog" :notice-id="currentNoticeId" prevent-resend />
  </v-timeline-item>
</template>

<script>
import historyMixins from "@/mixins/history.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import NoticeViewDialog from "@/components/notices/NoticeViewDialog.vue";
import VueCodeDiff from "vue-code-diff";

export default {
  name: "Timeline",
  components: { NoticeViewDialog, VueCodeDiff },
  mixins: [historyMixins, filterMixins, lookupMixins],
  props: {
    item: {
      type: Object,
      required: true
    },
    expanded: {
      type: Boolean,
      default: false
    },
    statusChangesOnly: {
      type: Boolean,
      default: false
    },
    providerId: {
      type: Number,
      required: true
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    providerName: {
      type: String,
      required: true
    },
    levyCollectionName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showNoticeViewDialog: false,
      currentNoticeId: null
    };
  },
  computed: {
    color() {
      switch (this.item.action) {
        case "LevyCollectionProviderCreated":
        case "LevyCollectionProviderStatusChanged":
          return "blue-grey";
        case "NoticeSent":
          return "cyan darken-3";
        case "InvoiceReversed":
          return "red darken-3";
        default:
          return "grey darken-1";
      }
    },
    icon() {
      switch (this.item.action) {
        case "LevyCollectionProviderCreated":
          return "add";
        case "LevyCollectionProviderRemoved":
          return "remove";
        case "LevyCollectionProviderUpdated":
          return "edit";
        case "LevyCollectionProviderLevyCalculated":
        case "LevyCollectionProviderLevyCleared":
          return "functions";
        case "LevyCollectionProviderStatusChanged":
          if (this.description === "Finalised") {
            return "power_off";
          }
          return "done";
        case "LevyCollectionOverrideCreated":
        case "LevyCollectionOverrideUpdated":
        case "LevyCollectionOverrideDeleted":
          return "change_history";
        case "InvoiceCreated":
        case "InvoiceUpdated":
        case "InvoiceReversed":
          return "attach_money";
        case "ProviderSapCustomerAccountCreated":
          return "settings";
        case "NoticeSent":
          return "email";
        case "ProviderNoteCreated":
        case "ProviderNoteUpdated":
        case "ProviderNoteDeleted":
          return "event_note";
        case "LevyEstimatorFirstOpened":
          return "cloud_done";
        default:
          return "";
      }
    },
    action() {
      var value = this.lookups.systemLogActionDescriptions[this.item.action];
      value = value.replace("Levy collection provider - ", "");
      value = value[0].toUpperCase() + value.substr(1);
      return value;
    },
    description() {
      var value = this.item.description.replace(`${this.levyCollectionName}, ${this.providerName}`, "");
      value = value.replace(` for ${this.levyCollectionName} - ${this.providerName}`, "");
      value = value.replace(", ", "").replace(": ", "");
      if (this.item.action === "LevyCollectionProviderCreated") {
        value = "In-scope";
      }
      return value;
    }
  },
  methods: {
    showNotice(noticeId) {
      this.currentNoticeId = noticeId;
      this.showNoticeViewDialog = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.expanding-header {
  cursor: pointer;
}
.v-timeline-item--left .v-card:after,
.v-timeline-item--left .v-card:before,
.v-timeline-item:nth-child(odd):not(.v-timeline-item--right) .v-card:after,
.v-timeline-item:nth-child(odd):not(.v-timeline-item--right) .v-card:before {
  top: 18px;
}
.v-timeline-item--right .v-card:after,
.v-timeline-item--right .v-card:before,
.v-timeline-item:nth-child(2n):not(.v-timeline-item--left) .v-card:after,
.v-timeline-item:nth-child(2n):not(.v-timeline-item--left) .v-card:before {
  top: 18px;
}
</style>